import {
  Component,
  Expressions,
  matchExpression,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  isUserComponentInst,
  getAncestorIds,
} from '@packages/utilities/core-elements';

import {
  EElement,
  ESelect,
  Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {identity} from '@packages/utilities/fp/utils';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {generateGuid} from '@packages/utilities/guid';
import {pair} from '@packages/utilities/Pair';

const applyOverridesFromInstance = (instanceElement: ExpressionElement) => {
  const instData = Expressions.getElementData(instanceElement);
  const instDataFields = Expressions.getRecord(instData);
  return matchExpression({
    Select:
      ({from, prop}: {from: Expression; prop: string}) =>
      (seenProps: Set<string>): [Set<string>, Expression] => {
        // Data prop names are not unique across components, so when expanding
        // overrides, we need to guard for infinite loops that would be caused
        // by nested overrides with the same ID.
        if (Expressions.isDataVariable(from) && !seenProps.has(prop)) {
          seenProps.add(prop);
          const propValue = instDataFields ? instDataFields[prop] : null;
          if (propValue) {
            return pair(seenProps, propValue);
          }
        }
        return pair(seenProps, ESelect({prop, from}));
      },
    _:
      (expression: Expression) =>
      (seenProps: Set<string>): [Set<string>, Expression] =>
        pair(seenProps, expression),
  });
};

const replaceElementExprWithComponentRoot = (
  expr: Expression,
  componentMap: Component.ComponentMap
): Expression | null => {
  const element = Expressions.getElement(expr);

  if (!element) {
    return null;
  }

  return replaceElementWithComponentRoot(element, componentMap);
};

const replaceElementWithComponentRoot = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): Expression | null => {
  // The element is not a user component instance, no need to expand
  if (!isUserComponentInst(componentMap, element)) {
    return null;
  }

  const rootComponent = componentMap.get(element.type);
  // The component is not found on the map,
  // return the original expr since we are unable to expand the component
  if (!rootComponent) {
    return null;
  }

  return Expressions.everywhereWithContext(applyOverridesFromInstance(element))(
    new Set<string>(),
    rootComponent.render
  );
};

type ElementPath = {
  element: ExpressionElement;
  path: ExpressionPath;
};

const findParentElementPath = (
  expression: Expression,
  elementId: string
): ElementPath | null => {
  const element = Expressions.getParent(expression, elementId);
  if (!element) return null;

  const path = Expressions.findPath(
    expression,
    (candidate) => Expressions.getElement(candidate) === element
  );

  return path ? {element, path} : null;
};

const reassignDuplicateElementIds = (
  expression: Expression,
  elementsToKeepUnchanged: ReadonlySet<ExpressionElement>
): Expression => {
  const seenIds = new Set(
    Array.from(elementsToKeepUnchanged, (element) => element.id)
  );

  return Expressions.everywhere(
    matchExpression({
      Element(element, elementExpression) {
        if (elementsToKeepUnchanged.has(element)) {
          return elementExpression;
        }
        if (!seenIds.has(element.id)) {
          seenIds.add(element.id);
          return elementExpression;
        }

        const id = generateGuid();
        seenIds.add(id);
        return EElement({...element, id});
      },
      _: identity,
    })
  )(expression);
};

type ExpandedUserComponentPath = {
  expression: Expression;
  parentElement: ExpressionElement;
};

/**
 * Expands only user component instances on the path from `parentElement` to
 * `element`. The selected occurrence gets a temporary ID so it can be followed
 * through binding rewrites, while the final visual parent's ID is preserved.
 */
export const expandUserComponentsAlongPath: (
  componentMap: Component.ComponentMap
) => (
  parentElement: ExpressionElement
) => (element: ExpressionElement) => ExpandedUserComponentPath | null =
  weakMemo((componentMap: Component.ComponentMap) =>
    weakMemo((parentElement: ExpressionElement) =>
      weakMemo((element: ExpressionElement) => {
        const parentExpression = EElement(parentElement);
        const elementPath = Expressions.findPath(
          parentExpression,
          (candidate) => Expressions.getElement(candidate) === element
        );
        if (!elementPath) return null;

        const elementMarkerId = generateGuid();
        const markedParentExpression = Expressions.setIn(
          parentExpression,
          elementPath,
          EElement({...element, id: elementMarkerId})
        );
        let expression = replaceElementExprWithComponentRoot(
          markedParentExpression,
          componentMap
        );
        if (!expression) return null;

        const parentComponent = componentMap.get(parentElement.type);
        if (!parentComponent) return null;

        const expandedComponents = new Set([parentComponent]);
        let parentPath = findParentElementPath(expression, elementMarkerId);

        while (
          parentPath &&
          isUserComponentInst(componentMap, parentPath.element)
        ) {
          const component = componentMap.get(parentPath.element.type);
          if (!component || expandedComponents.has(component)) return null;

          expandedComponents.add(component);
          const expandedParent = replaceElementWithComponentRoot(
            parentPath.element,
            componentMap
          );
          if (!expandedParent) break;

          expression = Expressions.updateIn(
            expression,
            parentPath.path,
            () => expandedParent
          );
          parentPath = findParentElementPath(expression, elementMarkerId);
        }

        if (!parentPath) return null;

        return {
          expression: reassignDuplicateElementIds(
            expression,
            new Set([parentPath.element])
          ),
          parentElement: parentPath.element,
        };
      })
    )
  );

/**
 * Replaces all user component instances with their roots in the provided element and applies overrides
 */
export const expandUserComponents: (
  componentMap: Component.ComponentMap
) => (element: ExpressionElement) => Expression = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((element: ExpressionElement): Expression => {
      const replaceComponentInstances = (e: Expression): Expression => {
        const maybeComponentRoot = replaceElementExprWithComponentRoot(
          e,
          componentMap
        );

        if (!maybeComponentRoot) {
          return e;
        }

        return Expressions.everywhere(replaceComponentInstances)(
          maybeComponentRoot
        );
      };

      const expr = EElement(element);

      return Expressions.everywhere(replaceComponentInstances)(expr);
    })
);

/**
 * Replaces all ancestral user component instances of `elementId` with their root, with overrides applied.
 *
 * This function is intended for use in locations where an expanded tree of elements that includes slot override elements is needed.
 *
 * Expands ancestral user component instances of `elementId` in `rootExpr` with overrides applied.
 * This function is useful for scenarios like Slot override elements. It ensures proper style rule
 * application by expanding only along the path to the descendant. Expanding all component instances
 * can result in duplicate elements when multiple instances of the same component exist in the tree.
 */
export const expandAncestorUserComponents: (
  rootExpr: Expression
) => (components: Component.ComponentMap) => (elementId: string) => Expression =
  weakMemo((rootExpr: Expression) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      singleMemo((elementId: string) => {
        const ancestorIds = getAncestorIds(rootExpr, elementId);
        const idsToKeepUnchanged = new Set(ancestorIds.concat(elementId));
        const deduplicateElementIds = reassignElementIds(idsToKeepUnchanged);

        // Walk the path of ancestor ids for the provided `elementId`, expanding any
        // user components encountered along the way (with overrides applied).
        return ancestorIds.reduce((currentExpr, ancestorId) => {
          const maybeComponentInstance = Expressions.getElementById(
            rootExpr,
            ancestorId
          );

          if (
            !maybeComponentInstance ||
            !isUserComponentInst(componentMap, maybeComponentInstance)
          ) {
            return currentExpr;
          }

          return Expressions.updateElement(
            maybeComponentInstance.id,
            (el) => {
              const maybeExpandedComponent = replaceElementWithComponentRoot(
                el,
                componentMap
              );

              return maybeExpandedComponent
                ? (Expressions.getElement(
                    deduplicateElementIds(maybeExpandedComponent)
                  ) ?? el)
                : el;
            },
            currentExpr
          );
        }, rootExpr);
      })
    )
  );

const reassignElementIds = (idsToKeepUnchanged: Set<string>) =>
  Expressions.everywhere(
    matchExpression({
      Element(element, expression) {
        if (idsToKeepUnchanged.has(element.id)) {
          return expression;
        }
        return EElement({...element, id: generateGuid()});
      },
      _: identity,
    })
  );
